import { AxisDomain, AxisInterval } from 'recharts/types/util/types';
import { IErrorMessageProps } from '../types';
import { COMPARISON_KEY_SUFFIX } from '../../../config/constants';
import { ChartViewType } from '../types';
import { AnalysisErrorStatus } from '../../../utils/apollo/graphqlSchema';

// default chart container height
export const CHART_CONTAINER_HEIGHT = 190;
// default DATA KEY to match data on X-axis
export const DATA_KEY = 'date' as const;
// use to match the data key in histogram chart.
export const HISTOGRAM_LABEL = 'histogram' as const;
// default YAXIS tick width
export const Y_AXIS_TICK_WIDTH = 40;
export const Y_AXIS_TICK_CATEGORY_WIDTH = 60;
export const DEFAULT_Y_AXIS_DOMAIN: AxisDomain = [0, 'auto'];
export const MAX_Y_AXIS_TICK_WIDTH_STRING = 'WWWW';

// try to use single chart margin for all charts to avoid too many condition checks
export const CHART_MARGIN = { top: 15, right: 5, bottom: 15, left: 5 };
// maximum number of visible groups on the chart
export const CHART_GROUPS_MAX_COUNT = 30;
// default number of visible groups on the chart
export const CHART_GROUPS_DEFAULT_COUNT = 10;

// Sankey chart on Path does not work well with more than 2 groups
export const CHART_GROUPS_PATH_MAX_COUNT = 2;
export const RETENTION_CHART_MAX_MEASURE_COUNT = 8;

// for bar chart, if data is 0, we still want to display a minimum bar for data to avoid
// the chart looks like missing data.
export const BAR_CHART_MIN_POINT_SIZE = 1;
export const BAR_CHART_STROKE_WIDTH = 2;

// the key of the base (overall) slice
export const OVERALL_SLICE_KEY = 'overall' as const;
export const OVERALL_COMPARISON_SLICE_KEY =
  `${OVERALL_SLICE_KEY}${COMPARISON_KEY_SUFFIX}` as const;

// segment slice key
export const SEGMENT_SLICE_KEY = 'segment' as const;

export const SUBJECT_EVENT_KEY = 'EVENT' as const;

export const PERMISSION_ERROR_MESSAGE =
  'You do not have permission to perform this action.';

export const CANT_DELETE_NON_EMPTY_FOLDER = 'Cannot delete a non-empty folder.';

export const DEFAULT_BAR_WIDTH = 18;
export const MAX_BAR_WIDTH = 54;

export const X_AXIS_DEFAULT_HEIGHT = 15;
export const X_AXIS_ROTATED_HEIGHT = 80;

// add 32 px to the right to avoid the last label being cut off
export const X_AXIS_PADDING_NUMBER = 32;
export const X_AXIS_PADDING = {
  left: 0,
  right: X_AXIS_PADDING_NUMBER,
};

/**
 * Get the padding for the X axis based on the presence of left and right axes
 * If YAxis exists, no need to add extra padding because the YAxis already allocate the space
 * This is only will be used for charts contains dual axes: Line & Bar charts for now
 */
export const getXAxisPadding = ({
  hasLeftAxis,
  hasRightAxis,
}: {
  hasLeftAxis: boolean;
  hasRightAxis: boolean;
}) => {
  return {
    left: hasLeftAxis ? 0 : X_AXIS_PADDING_NUMBER,
    right: hasRightAxis ? 0 : X_AXIS_PADDING_NUMBER,
  };
};

export const DEFAULT_X_AXIS_CONFIG = {
  tickSize: 3,
  height: X_AXIS_DEFAULT_HEIGHT,
  interval: 'equidistantPreserveStart' as AxisInterval,
  padding: X_AXIS_PADDING,
};

// Chart error messages
export const CHART_ERROR_MESSAGES: Record<
  Exclude<ChartViewType, ChartViewType.Default>,
  IErrorMessageProps
> = {
  [ChartViewType.NoData]: {
    title: 'No data was found for your query',
    description:
      'Try expanding your date range, adjusting your query, or contact Kubit support',
  },
  [ChartViewType.Cancelled]: {
    title: 'Query Cancelled',
    description:
      'The query was cancelled successfully. You can modify the parameters and try again.',
  },
  [ChartViewType.PermissionError]: {
    title: 'Permission Error',
    description: PERMISSION_ERROR_MESSAGE,
  },
  [ChartViewType.KobolError]: {
    title: 'Error occurred with the report logic',
    description: 'Sorry, there was an error processing your report.',
  },
  [ChartViewType.CustomError]: {
    title: 'An error has occurred',
    description:
      'We apologize for the inconvenience. Please contact Kubit support.',
  },
  [ChartViewType.FallbackError]: {
    title: 'Sorry, something went wrong',
    description:
      'We apologize for the inconvenience. Please contact Kubit support.',
  },
};

// Kobol error messages
export const KOBOL_ERROR_MESSAGES: Record<
  AnalysisErrorStatus,
  IErrorMessageProps
> = {
  [AnalysisErrorStatus.Timeout]: {
    title:
      'Your report has exceeded the timeout limit set by your administrator',
    description:
      'The maximum query timeout has been reached. Please shorten the dashboard/report duration ' +
      'or contact your Kubit Administrator to extend the query timeout.',
  },
  [AnalysisErrorStatus.EntityUpgradeError]: {
    title: 'Entity Update Error',
  },
  [AnalysisErrorStatus.ServiceError]: {
    title: 'Service Error Encountered',
  },
  [AnalysisErrorStatus.Unexpected]: {
    title: 'Unexpected Error Occurred',
  },
  [AnalysisErrorStatus.Unknown]: {
    title: 'Unknown Error Detected',
  },
  [AnalysisErrorStatus.SqlError]: {
    title: 'SQL Error in Report Logic',
  },
};
